import { Box, Flex, Text } from '@chakra-ui/react';
import type { CSSProperties } from 'react';

// Keep the native input focusable + announced by AT (unlike display:none) while
// hiding it visually behind the custom control.
const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
}

/** Figma-styled switch row for a boolean setting. */
export function Toggle({ checked, onChange, label, description }: ToggleProps) {
  return (
    <Flex
      as="label"
      align="center"
      gap="3"
      px="2.5"
      py="2"
      borderWidth="1px"
      borderColor="figma.border"
      borderRadius="md"
      cursor="pointer"
      _hover={{ borderColor: 'figma.borderStrong' }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.currentTarget.checked)}
        style={VISUALLY_HIDDEN}
      />
      <Box flex="1" minW="0">
        <Text fontSize="xs" color="figma.text" fontWeight="500">
          {label}
        </Text>
        {description && (
          <Text fontSize="2xs" color="figma.textMuted" mt="0.5">
            {description}
          </Text>
        )}
      </Box>
      <Box
        w="32px"
        h="18px"
        borderRadius="full"
        bg={checked ? 'figma.accent' : 'figma.panelHover'}
        transition="background 0.15s"
        flexShrink={0}
        p="2px"
        display="flex"
        justifyContent={checked ? 'flex-end' : 'flex-start'}
      >
        <Box w="14px" h="14px" borderRadius="full" bg="white" />
      </Box>
    </Flex>
  );
}
